import { c as _c } from "react/compiler-runtime";
import { DateTime } from 'luxon';
import { pipe } from 'remeda';
import { usePortal } from 'components/portal/portal';
import { compactObj, keyBy } from 'shared/collection';
import { Prettify, safeParseList, zx } from 'shared/type';
import { useQuerySb } from 'utils/db/useQuerySb';
import { z, ZodSchema } from 'zod';
import { showError } from 'components/notify';
import { useCallback, useMemo } from 'react';
import { match } from 'ts-pattern';
import { listMods } from 'components/hooks/useUIState';
import { sb, type Database } from 'utils/db';
import { doTo } from 'shared/promise';
export const reactionTypes = ['vote', 'participation'] as const;
export const zReactionCommon = z.object({
  type: z.enum(reactionTypes)
});
export const zReactionTypes = {
  vote: zReactionCommon.extend({
    type: z.literal('vote'),
    vote: z.number().int().min(-2).max(2)
  }),
  participation: zReactionCommon.extend({
    type: z.literal('participation'),
    status: z.boolean().optional()
  })
} satisfies { [T in ReactionType]: ZodSchema };
export const zReaction = z.discriminatedUnion('type', [zReactionTypes.vote, zReactionTypes.participation]);
type ReactionVote = z.infer<typeof zReactionTypes.vote>;
export type ReactionParticipation = z.infer<typeof zReactionTypes.participation>;
type ReactionType = (typeof reactionTypes)[number];
export type ReactionCfg<T extends ReactionType> = z.infer<(typeof zReactionTypes)[T]>;
export const zReactionSummary = z.object({
  my_reaction_cfg: zx.nullishToUndefined(zReaction.optional()),
  my_reaction_id: zx.nullishToUndefined(z.string().optional()),
  type: z.enum(reactionTypes),
  question_id: z.string(),
  subquestion_id: zx.nullishToUndefined(z.string().optional()),
  vote_total: zx.nullishToUndefined(z.number().default(0)),
  participation_total: zx.nullishToUndefined(z.number().default(0)),
  participants: zx.nullishToUndefined(z.array(z.string()).default([]))
});
type ReactionSummary = z.infer<typeof zReactionSummary>;
export type ReactionSummaryOfType<T extends ReactionType> = Prettify<z.infer<typeof zReactionSummary> & {
  type: T;
  my_reaction_cfg?: ReactionCfg<T>;
}>;
export const zReactionRow = z.object({
  id: z.string(),
  cfg: zReaction,
  question_id: z.string(),
  subquestion_id: zx.nullishToUndefined(z.string().optional()),
  created_by: z.string(),
  updated_at: z.string()
});
export type ReactionRow<T extends ReactionType> = z.infer<typeof zReactionRow> & {
  cfg: ReactionCfg<T>;
};
const summaryId = (r: Pick<ReactionSummary, 'question_id' | 'subquestion_id' | 'type'>) => [r.question_id, r.subquestion_id, r.type].join('|');
export const reactionSummarySb = async <Types extends ReactionType[] = ReactionType[],>({
  workspace_id,
  question_id,
  types
}: {
  workspace_id: string;
  question_id?: string;
  types?: Types;
}) => {
  type Args = Database['public']['Functions']['question_reaction_summary_fn']['Args'];
  return await sb.rpc('question_reaction_summary_fn', compactObj({
    p_workspace_id: workspace_id,
    p_question_id: question_id,
    p_types: types
  }) as Args).then(r => {
    if (r.error) throw r.error;
    return safeParseList(zReactionSummary, r.data).map(({
      my_reaction_cfg,
      ...r
    }) => ({
      ...r,
      id: summaryId(r),
      type: r.type as Types[number],
      my_reaction_cfg: my_reaction_cfg as ReactionCfg<Types[number]>
    }));
  });
};
type ReactionSummaryFetched = Awaited<ReturnType<typeof reactionSummarySb>>[number];
export type ReactionRowForSave = Pick<z.infer<typeof zReactionRow>, 'question_id' | 'subquestion_id' | 'cfg' | 'created_by'>;
export const saveReactionAndUpdateSummary = async ({
  row,
  setQueryData
}: {
  row: ReactionRowForSave;
  setQueryData: (updater: (prevList: ReactionSummaryFetched[] | undefined) => ReactionSummaryFetched[] | undefined) => void;
}) => {
  const toUpsert = {
    ...row,
    updated_at: DateTime.utc().toISO()
  };
  const {
    question_id,
    subquestion_id,
    cfg
  } = row;
  const {
    error
  } = await sb.from('reaction').upsert(toUpsert, {
    onConflict: 'reaction_natural_key'
  });
  if (error) showError(error, 'Error saving reaction');
  setQueryData(prevList => {
    if (!prevList) return prevList;
    // optimistically change the summary row
    const list = listMods(prevList);
    const defaultSummary = pipe(zReactionSummary.parse({
      type: cfg.type,
      question_id,
      subquestion_id
    }), r => ({
      id: summaryId(r),
      ...r
    }));
    const prev = list.get(defaultSummary.id) ?? defaultSummary;
    const newList = list.upsert({
      ...prev,
      my_reaction_cfg: cfg,
      my_reaction_id: undefined,
      ...match(cfg).with({
        type: 'vote'
      }, ({
        vote
      }) => ({
        vote_total:
        // update summary row by working out the difference between our old and new vote
        prev.vote_total + vote - ((prev.my_reaction_cfg as ReactionVote)?.vote ?? 0)
      })).otherwise(() => {})
    });
    return newList as ReactionSummaryFetched[];
  });
};
export const useQuestionVotesQuery = t0 => {
  const $ = _c(8);
  const {
    question_id,
    select,
    disabled
  } = t0;
  const {
    workspace_id
  } = usePortal();
  let t1;
  if ($[0] !== question_id) {
    t1 = ["useVotes", {
      question_id
    }];
    $[0] = question_id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== question_id || $[3] !== workspace_id) {
    t2 = async () => await reactionSummarySb({
      workspace_id,
      question_id,
      types: ["vote"]
    });
    $[2] = question_id;
    $[3] = workspace_id;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const t3 = !!question_id && !disabled;
  let t4;
  if ($[5] !== select || $[6] !== t3) {
    t4 = {
      enabled: t3,
      select
    };
    $[5] = select;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return useQuerySb("reaction", t1, t2, t4);
};

/** voting summary data for sub-questions in a question */
export const useQuestionVotes = t0 => {
  const $ = _c(8);
  const {
    question_id,
    disabled
  } = t0;
  let t1;
  if ($[0] !== disabled || $[1] !== question_id) {
    t1 = {
      question_id,
      disabled
    };
    $[0] = disabled;
    $[1] = question_id;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    data,
    isLoading
  } = useQuestionVotesQuery(t1);
  let t2;
  if ($[3] !== data) {
    t2 = subquestion_id => data?.find(r => r.subquestion_id == subquestion_id);
    $[3] = data;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const getVote = t2;
  let t3;
  if ($[5] !== getVote || $[6] !== isLoading) {
    t3 = {
      getVote,
      isLoading
    };
    $[5] = getVote;
    $[6] = isLoading;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
};
export type UseQuestionVotes = ReturnType<typeof useQuestionVotes>;

/** voting summary data and ability to save a vote on a sub-question */
export const useVote = t0 => {
  const $ = _c(19);
  const {
    question_id,
    subquestion_id,
    disabled
  } = t0;
  const {
    profile
  } = usePortal();
  let t1;
  if ($[0] !== subquestion_id) {
    t1 = rs => rs.filter(r => r.subquestion_id == subquestion_id);
    $[0] = subquestion_id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== disabled || $[3] !== question_id || $[4] !== t1) {
    t2 = {
      question_id,
      select: t1,
      disabled
    };
    $[2] = disabled;
    $[3] = question_id;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    data,
    setQueryData,
    isLoading
  } = useQuestionVotesQuery(t2);
  let t3;
  if ($[6] !== profile || $[7] !== question_id || $[8] !== setQueryData || $[9] !== subquestion_id) {
    t3 = async cfg => {
      if (!question_id) {
        throw Error("Need question_id to save reaction");
      }
      await saveReactionAndUpdateSummary({
        row: {
          question_id,
          subquestion_id,
          cfg: {
            ...cfg,
            type: "vote"
          },
          created_by: profile.id
        },
        setQueryData: setQueryData as any
      });
    };
    $[6] = profile;
    $[7] = question_id;
    $[8] = setQueryData;
    $[9] = subquestion_id;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const save = t3;
  let t4;
  bb0: {
    if (data == null) {
      t4 = undefined;
      break bb0;
    }
    let t5;
    if ($[11] !== data[0] || $[12] !== question_id || $[13] !== subquestion_id) {
      t5 = data[0] ?? zReactionSummary.parse({
        type: "vote",
        question_id,
        subquestion_id
      });
      $[11] = data[0];
      $[12] = question_id;
      $[13] = subquestion_id;
      $[14] = t5;
    } else {
      t5 = $[14];
    }
    t4 = t5;
  }
  const vote = t4;
  let t5;
  if ($[15] !== isLoading || $[16] !== save || $[17] !== vote) {
    t5 = {
      save,
      isLoading,
      vote
    };
    $[15] = isLoading;
    $[16] = save;
    $[17] = vote;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  return t5;
};
export type UseVote = ReturnType<typeof useVote>;